/**
 * ── A MARCA DA ÁREA: O ÚNICO LUGAR QUE DESEMBRULHA O QUE O SERVIDOR MANDA ────
 *
 * ## O defeito que este arquivo existe para impedir de voltar
 *
 * Medido em produção em 23/09/2026, na área «Comunidade AutoNext»
 * (https://comunidade.ericorenato.ia.br): o cabeçalho do aluno renderizava
 * literalmente
 *
 *     <img src="[object Object]">
 *
 * e a aba do navegador ficava com o ícone de fábrica. Nada no console, nada no
 * `tsc`, nada em cerca nenhuma — só um logo que não aparece.
 *
 * A causa não foi um erro de digitação. `GET /a/:slug` montava a marca com
 * `capas.get(id)`, e o mapa era `Map<string, string>` no dia em que a linha foi
 * escrita. Quando a vitrine passou a precisar de `srcset` para o cartaz, a
 * chamada trocou de `resolverCapas` para `resolverCapasServidas` — e o mapa
 * passou a devolver `CapaServida`, um OBJETO. A linha da marca não mudou uma
 * letra, o tipo dela do lado da tela continuou dizendo `string | null`, e
 * `String({url: …})` é `"[object Object]"`, que é uma string perfeitamente
 * válida para o atributo `src`. O navegador pediu `/a/[object%20Object]`, levou
 * 404, e desenhou o ícone de imagem partida.
 *
 * ## Por que a resposta é um MÓDULO, e não um `.url` em cada tela
 *
 * Porque `.url` em cada tela é exatamente o desenho que produziu o defeito:
 * quatro leitores, cada um desembrulhando do seu jeito, e o quinto nasce sem
 * saber. Aqui há **uma** função que recebe o que veio pela rede e devolve o que
 * a tela usa — e as duas formas são **estruturalmente diferentes de propósito**:
 *
 * - o que vem da rede tem `url` (`ImagemDaMarca`);
 * - o que a tela usa tem `src` (`ImagemNaTela`).
 *
 * Com isso o TypeScript recusa `src={marca.logo}` (um objeto onde se espera
 * `string`) **e** recusa passar o objeto cru para quem espera o desembrulhado
 * (falta `src`). O defeito volta como erro de compilação, não como pixel.
 *
 * ## Por que tolera `string` em tempo de execução
 *
 * Porque a rota do curso e a tela de entrar ainda servem o endereço direto
 * (`resolverCapas`), porque um cliente com a página velha em cache conversa com
 * a API nova, e porque o contrário — a API velha com a tela nova — acontece
 * durante todo deploy. Um desembrulhador que só soubesse uma das formas trocaria
 * um logo ausente por outro. O TIPO é estrito; o RUNTIME é tolerante.
 */

/**
 * O que a API manda por campo da marca — uma `CapaServida` (ver `capas-da-area.ts`).
 *
 * Todo campo além de `url` é opcional porque a rota do curso e a tela de entrar
 * mandam só o endereço, e porque `srcset` e as dimensões só existem depois que
 * o derivador passou.
 */
export interface ImagemDaMarca {
  /** O ORIGINAL assinado. É o único campo que sempre existe. */
  url: string;
  /** `<url> 320w, <url> 640w, …`. Vazio enquanto não houver derivado. */
  srcset?: string;
  /** O degrau do meio. Cai no original quando não há derivado. */
  padrao?: string;
  /** As dimensões do ORIGINAL. `null` até o derivador as escrever. */
  largura?: number | null;
  altura?: number | null;
  /** O arquivo se MEXE sozinho — hoje, `image/gif`. */
  animada?: boolean;
}

/**
 * A mesma imagem, na forma que um `<img>` usa.
 *
 * `src` e `srcSet` (e nunca `url`) não é preciosismo de nome: é o que faz o
 * compilador separar "o que veio da rede" de "o que já foi desembrulhado".
 */
export interface ImagemNaTela {
  /** O ORIGINAL. É ele que vai para o favicon e para o `og:image` — ver abaixo. */
  src: string;
  /** Vazio quando não há degrau nenhum. */
  srcSet: string;
  /** O degrau do meio, ou o original. É o `src` de um `<img>` de tela. */
  padrao: string;
  largura: number | null;
  altura: number | null;
  animada: boolean;
}

/** Os três arquivos da marca, já desembrulhados. Nulo = o cliente não escolheu. */
export interface MarcaNaTela {
  logo: ImagemNaTela | null;
  favicon: ImagemNaTela | null;
  compartilhamento: ImagemNaTela | null;
}

const NUMERO_OU_NULO = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;

/**
 * Desembrulha UM campo da marca. Aceita o objeto, o endereço, e o nada.
 *
 * Devolve `null` — e não um objeto de fachada — sempre que não houver endereço
 * de verdade: quem chama já tem de saber desenhar a ausência (o nome da área),
 * e um `src=""` é um pedido à própria página que o navegador faz de graça.
 */
export function imagemDaMarca(bruto: unknown): ImagemNaTela | null {
  if (typeof bruto === 'string') {
    const url = bruto.trim();
    return url === ''
      ? null
      : { src: url, srcSet: '', padrao: url, largura: null, altura: null, animada: false };
  }
  if (bruto === null || typeof bruto !== 'object') return null;

  const o = bruto as Partial<ImagemDaMarca>;
  const url = typeof o.url === 'string' ? o.url.trim() : '';
  if (url === '') return null;

  const padrao = typeof o.padrao === 'string' && o.padrao !== '' ? o.padrao : url;
  return {
    src: url,
    srcSet: typeof o.srcset === 'string' ? o.srcset : '',
    padrao,
    largura: NUMERO_OU_NULO(o.largura),
    altura: NUMERO_OU_NULO(o.altura),
    animada: o.animada === true,
  };
}

/** Os três campos de uma vez. Corpo antigo, campo ausente e `null` caem em nulo. */
export function marcaDaArea(bruta: unknown): MarcaNaTela {
  const m = (bruta ?? {}) as Record<string, unknown>;
  return {
    logo: imagemDaMarca(m.logo),
    favicon: imagemDaMarca(m.favicon),
    compartilhamento: imagemDaMarca(m.compartilhamento),
  };
}

/** O que um `<img>` precisa receber. `undefined` = o atributo não sai no HTML. */
export interface AtributosDaImagem {
  src: string;
  srcSet?: string;
  sizes?: string;
  width?: number;
  height?: number;
}

/**
 * Os atributos do `<img>` do logo, para uma altura de desenho conhecida.
 *
 * ## Por que o `srcset` só sai acompanhado de `sizes`
 *
 * Porque `srcset` com descritor de largura e **sem** `sizes` faz o navegador
 * presumir `100vw`: num monitor de 1920 ele escolheria o degrau de 1920 para
 * desenhar um logo de 24px de altura — o oposto exato do que o `srcset` existe
 * para fazer. O `sizes` correto é calculável aqui e em nenhum outro lugar: a
 * altura é a do modelo de cabeçalho (`ALTURA_DA_MARCA`), a largura sai da
 * proporção do original, e a proporção só se conhece com `largura` e `altura`
 * gravadas. Sem elas, servir o original é a escolha certa — e é a que já
 * existia.
 *
 * ## Por que `width` e `height` saem no HTML mesmo com a altura fixada no CSS
 *
 * Porque são eles que dão ao navegador a **proporção** antes de o arquivo
 * chegar. Com `height` no estilo e `width: auto` na classe, o par de atributos
 * vira `aspect-ratio` e a caixa do logo nasce com a largura final: o nome da
 * área ao lado não salta quando a imagem carrega. Sem eles a largura é zero até
 * o byte chegar, e a barra inteira se reorganiza no meio do primeiro desenho.
 */
export function atributosDoLogo(img: ImagemNaTela, alturaEmPx: number): AtributosDaImagem {
  const proporcional =
    img.largura !== null && img.altura !== null && img.altura > 0
      ? Math.round(alturaEmPx * (img.largura / img.altura))
      : null;

  return {
    /* O degrau do meio, e não o original: um logo desenhado a 24px não precisa
       do PNG de 2000px que o cliente subiu. Cai no original sozinho quando não
       há derivado (SVG e GIF nunca têm — ver `derivados-do-asset.ts`). */
    src: img.padrao,
    ...(img.srcSet !== '' && proporcional !== null
      ? { srcSet: img.srcSet, sizes: `${proporcional}px` }
      : {}),
    ...(img.largura !== null && img.altura !== null
      ? { width: img.largura, height: img.altura }
      : {}),
  };
}
